import thgService from "@/services/thg/services/thgService";
import {
  ThgBankingViewModelGen,
  ThgRegistrationDtoGen,
  ThgThgViewModelGen,
  ThgTransferThgDtoGen,
  ThgUpdateThgDtoGen,
  ThgUpdateThgRegistrationImagesDtoGen
} from "@/services/thg/v1/data-contracts";
import store from "@/store/VuexPlugin";
import Vue from "vue";
import { Action, getModule, Module, Mutation, VuexModule } from "vuex-module-decorators";
import { IUpdateThgStatusDto } from "../interface/thg/thg-update-status.interface";
import { ThgPaginationModule } from "./thg-pagination.store";
import { ThgCreateModule } from "./thg.create.store";
import { UserAdministrationModule } from "./user.administration.store";

@Module({
  dynamic: true,
  namespaced: true,
  name: "thgPortal",
  store
})
export class ThgPortalStore extends VuexModule {
  /**
   * All fetched THGs
   */
  private _allThgs: ThgThgViewModelGen[] = [];

  get allThgs(): ThgThgViewModelGen[] {
    return this._allThgs;
  }

  /**
   * true when _allThgs were loaded
   */
  private _allThgsLoading = true;

  get allThgsLoading(): boolean {
    return this._allThgsLoading;
  }

  /**
   * The selected Thg
   */
  private _selectedThg: ThgThgViewModelGen = {} as any;

  get selectedThg(): ThgThgViewModelGen {
    return this._selectedThg;
  }

  /**
   * The Bankinginformation of the selected Thg
   */
  private _bankingInformation: ThgBankingViewModelGen = {} as any;

  get bankingInformation() {
    return this._bankingInformation;
  }

  /**
   * is true if there are thg work items that have to be labeled
   */
  private _hasNextWorkItem = true;
  get hasNextWorkItem(): boolean {
    return this._hasNextWorkItem;
  }

  /**
   * is true if the labeling process is started
   */
  private _isWorkStarted = false;

  get isWorkStarted(): boolean {
    return this._isWorkStarted;
  }

  /**
   * the thg workitem
   */
  private _thgWorkitem: ThgThgViewModelGen = {} as any;

  /**
   * get the current thg workitem
   */
  get thgWorkitem(): ThgThgViewModelGen {
    return this._thgWorkitem;
  }

  /**
   * The amount of the thg quote
   */
  private _amountOfCredit = 350;

  /**
   * Get the amount of credit
   */
  get amountOfCredit() {
    return this._amountOfCredit;
  }

  @Mutation
  _mutateAmountOfCredit(value: number) {
    this._amountOfCredit = value;
  }

  @Action
  setAmountOfCredit(value: number) {
    this.context.commit("_mutateAmountOfCredit", value);
  }

  @Mutation
  _mutateBankingInformation(bankingInformation: ThgBankingViewModelGen) {
    this._bankingInformation = bankingInformation;
  }

  @Action
  async getBankingInformation(): Promise<ThgBankingViewModelGen> {
    this.context.commit("_mutateBankingInformation", {} as any);
    const bankingViewmodel: ThgBankingViewModelGen = await thgService.getBankingInformation(
      this.selectedThg.partnerId,
      this.selectedThg.id
    );
    this.context.commit("_mutateBankingInformation", bankingViewmodel);
    return bankingViewmodel;
  }

  @Mutation
  _mutateIsWorkStarted(value: boolean) {
    this._isWorkStarted = value;
  }

  @Action
  updateIsWorkStarted(value: boolean) {
    this.context.commit("_mutateIsWorkStarted", value);
  }

  @Mutation
  _mutateHasNextWorkItem(value: boolean) {
    this._hasNextWorkItem = value;
  }

  @Action
  updateHasNextWorkItem(value: boolean) {
    this.context.commit("_mutateHasNextWorkItem", value);
  }

  @Mutation
  _mutateAllThgs(allThgs: ThgThgViewModelGen[]) {
    this._allThgs = allThgs;
  }

  @Mutation
  _mutateAllThgsLoading(loading: boolean) {
    this._allThgsLoading = loading;
  }

  @Action
  setAllThgsLoading(loading: boolean) {
    this.context.commit("_mutateAllThgsLoading", loading);
  }

  @Action
  async getThgByPartnerId(partnerId: string) {
    this.context.commit("_mutateAllThgs", []);
    this.context.commit("_mutateSelectedThg", {});
    const allThgs = (await thgService.getAllThgs(partnerId)) as any;
    allThgs.sort(function(a: any, b: any) {
      return a.timestamp.created > b.timestamp.created ? -1 : a.timestamp.created > b.timestamp.created ? 1 : 0;
    });
    this.context.commit("_mutateAllThgs", allThgs);
  }

  @Action
  async setAllThgs(thgs: ThgThgViewModelGen[]) {
    this.context.commit("_mutateAllThgs", thgs);
  }

  @Mutation
  _mutateSelectedThg(thg: ThgThgViewModelGen) {
    this._selectedThg = thg;
  }

  @Mutation
  _mutateSelectedThgInAllThg(thg: ThgThgViewModelGen) {
    const index = this._allThgs.findIndex(thgListItem => thgListItem.id === thg.id);
    if (index != -1) {
      this._allThgs.splice(index, 1, thg);
    }
  }

  @Action
  async getSelectedThg(thgId: string): Promise<ThgThgViewModelGen> {
    const selectedThg: ThgThgViewModelGen = await thgService.getThg(thgId);
    this.context.commit("_mutateSelectedThg", selectedThg);
    return selectedThg;
  }

  @Action
  async getSelectedThgByPartnerID(data: { thgId: string; partnerId: string }): Promise<ThgThgViewModelGen> {
    const selectedThg: ThgThgViewModelGen = await thgService.getThgByPartnerId(data.thgId, data.partnerId);
    this.context.commit("_mutateSelectedThg", selectedThg);
    return selectedThg;
  }

  @Mutation
  _mutateThgWorkitem(thg: ThgThgViewModelGen) {
    this._thgWorkitem = thg;
  }

  @Action
  async getThgAsWorkItem(): Promise<ThgThgViewModelGen> {
    const thgWorkitem: ThgThgViewModelGen = await thgService.getThgWorkItem();
    if (thgWorkitem) {
      ThgPortalModule.updateHasNextWorkItem(true);
      await UserAdministrationModule.getSelectedUser(thgWorkitem.userId);
      this.context.commit("_mutateThgWorkitem", thgWorkitem);
    } else {
      ThgPortalModule.updateHasNextWorkItem(false);
      Vue.$toast.info("Keine weiteren Workitems.");
    }

    return thgWorkitem;
  }

  @Action
  async updateThgWorkitemStatus(updateStatusStoreDto: IUpdateThgStatusDto) {
    await thgService.updateStatus(
      this._thgWorkitem.partnerId,
      this._thgWorkitem.id,
      updateStatusStoreDto.dto,
      updateStatusStoreDto.noUserNotification
    );
  }

  @Action
  async updateWorkitemRegistration(updateRegistration: ThgRegistrationDtoGen): Promise<ThgThgViewModelGen> {
    const selectedThg: ThgThgViewModelGen = await thgService.updateRegistration(
      this.thgWorkitem.partnerId,
      this.thgWorkitem.id,
      updateRegistration
    );
    this.context.commit("_mutateSelectedThg", selectedThg);
    this.context.commit("_mutateSelectedThgInAllThg", selectedThg);
    return selectedThg;
  }

  @Action
  async updateSelectedThgStatus(updateStatusStoreDto: IUpdateThgStatusDto): Promise<ThgThgViewModelGen> {
    const selectedThg: ThgThgViewModelGen = await thgService.updateStatus(
      this.selectedThg.partnerId,
      this.selectedThg.id,
      updateStatusStoreDto.dto,
      updateStatusStoreDto.noUserNotification
    );
    this.context.commit("_mutateSelectedThg", selectedThg);
    this.context.commit("_mutateSelectedThgInAllThg", selectedThg);
    ThgPaginationModule.replaceInList(selectedThg);
    return selectedThg;
  }

  @Action
  async updateThg(dto: ThgUpdateThgDtoGen): Promise<ThgThgViewModelGen> {
    const selectedThg: ThgThgViewModelGen = await thgService.updateThg(
      this.selectedThg.partnerId,
      this.selectedThg.id,
      dto
    );
    this.context.commit("_mutateSelectedThg", selectedThg);
    this.context.commit("_mutateSelectedThgInAllThg", selectedThg);
    return selectedThg;
  }

  @Action
  async transferThgToOtherPartner(dto: ThgTransferThgDtoGen): Promise<ThgThgViewModelGen> {
    const selectedThg: ThgThgViewModelGen = await thgService.transferThgToOtherPartner(
      this.selectedThg.partnerId,
      this.selectedThg.id,
      dto
    );
    this.context.commit("_mutateSelectedThg", selectedThg);
    return selectedThg;
  }

  @Action
  async updateRegistration(updateRegistration: ThgRegistrationDtoGen): Promise<ThgThgViewModelGen> {
    const selectedThg: ThgThgViewModelGen = await thgService.updateRegistration(
      this.selectedThg.partnerId,
      this.selectedThg.id,
      updateRegistration
    );
    this.context.commit("_mutateSelectedThg", selectedThg);
    this.context.commit("_mutateSelectedThgInAllThg", selectedThg);
    return selectedThg;
  }

  @Action
  async updateRegistrationImages(): Promise<ThgThgViewModelGen> {
    const thgUpdateRegistrationImagesDto: ThgUpdateThgRegistrationImagesDtoGen = {
      registrationImages: []
    };

    for (const image of ThgCreateModule.registrationImageFront) {
      thgUpdateRegistrationImagesDto.registrationImages.push(image.uploadId);
    }

    for (const image of ThgCreateModule.registrationImageBack) {
      thgUpdateRegistrationImagesDto.registrationImages.push(image.uploadId);
    }

    const selectedThg: ThgThgViewModelGen = await thgService.updateRegistrationImages(
      this.selectedThg.partnerId,
      this.selectedThg.id,
      thgUpdateRegistrationImagesDto
    );

    this.context.commit("_mutateSelectedThg", selectedThg);
    this.context.commit("_mutateSelectedThgInAllThg", selectedThg);

    ThgCreateModule.resetThg();

    return selectedThg;
  }

  @Action
  setSelectedThg(thg: ThgThgViewModelGen) {
    this.context.commit("_mutateSelectedThg", thg);
  }
}

export const ThgPortalModule = getModule(ThgPortalStore);
